const JOURNALS_CONTAINER_SELECTOR = ".easy-entity-journals";
const JOURNALS_PAGES_SELECTOR = ".easy-entity-journals-pages";
const LOAD_MORE_LINK_SELECTOR = ".journals_link_load_more";

const clickedLink = (event: Event): HTMLAnchorElement | null => {
  const element = (event.currentTarget ?? event.target) as Element | null;
  return element?.closest("a") ?? null;
};

/**
 * The journals endpoint renders the same trusted markup as the entity tabs: inline `onclick` handlers
 * (journal menu, copy link, show all) and a reactions `<script>` per journal, so it is inserted as is.
 * The contextual fragment keeps the handlers live; the scripts get a fresh copy that runs exactly once
 * on insertion, in browsers and in jsdom alike (jsdom never starts the parsed ones).
 */
const fetchHtmlFragment = async (url: string): Promise<DocumentFragment> => {
  const response = await fetch(url);
  const fragment = document.createRange().createContextualFragment(await response.text());
  fragment.querySelectorAll("script").forEach(parsedScript => {
    const script = document.createElement("script");
    Array.from(parsedScript.attributes).forEach(({ name, value }) => script.setAttribute(name, value));
    script.textContent = parsedScript.textContent;
    parsedScript.replaceWith(script);
  });
  return fragment;
};

const announceNewDom = (target: Element): void => {
  target.dispatchEvent(new CustomEvent("erui_new_dom", { bubbles: true }));
};

/**
 * "Show all" link handler - replaces the limited journals list with the complete history.
 */
export const easyJournalLoadAllHistory = async (event: Event): Promise<void> => {
  event.preventDefault();

  try {
    const link = clickedLink(event);
    const historyElement = link?.closest(JOURNALS_CONTAINER_SELECTOR);
    const wrapper = historyElement?.parentElement;
    if (!link || !historyElement || !wrapper) return;

    wrapper.replaceChildren(await fetchHtmlFragment(link.href));
    announceNewDom(wrapper);
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * "Load more" link handler for paginated journals - inserts the next (older) page into the current list.
 * Older journals go above the list in chronological order (`data-direction="prepend"`)
 * and below it in reverse order (`data-direction="append"`).
 */
export const easyJournalLoadMoreHistory = async (event: Event): Promise<void> => {
  event.preventDefault();

  try {
    const link = clickedLink(event);
    const pages = link?.closest(JOURNALS_PAGES_SELECTOR);
    const container = pages?.querySelector(JOURNALS_CONTAINER_SELECTOR);
    if (!link || !pages || !container) return;

    const fragment = await fetchHtmlFragment(link.href);
    const loadedJournals = Array.from(fragment.querySelector(JOURNALS_CONTAINER_SELECTOR)?.children ?? []);
    const nextLink = fragment.querySelector<HTMLAnchorElement>(LOAD_MORE_LINK_SELECTOR);

    if (link.dataset.direction === "append") {
      container.append(...loadedJournals);
    } else {
      container.prepend(...loadedJournals);
    }

    if (nextLink) {
      link.replaceWith(nextLink);
    } else {
      link.remove();
    }

    announceNewDom(container);
  } catch (error) {
    console.error("Error loading more journals:", error);
  }
};
